import { defineStore } from 'pinia'

/**
 * The pricing filters shared by the three customer lists.
 *
 * One store rather than three copies: all three tabs ask the same three
 * questions, and the filters deliberately persist across a tab switch — having
 * filtered to a segment, seeing the same segment's insurance customers and private
 * customers is usually the point.
 *
 * The per-tab `searchText` and `listStateFilter` stay on their own stores,
 * since those already existed and are genuinely per-list.
 */
export const useCustomerPricingFilterStore = defineStore(
  'customerPricingFilter',
  {
    state: () => ({
      /** Show only customers on these segments. Empty = no segment filter. */
      segmentIDs: [] as string[],
      /** Show only customers on these currencies. Empty = no currency filter. */
      currencyCodes: [] as string[],
      /** Show only customers with pricing of their own on top of the segment. */
      modifiedOnly: false,
      /**
       * Hide internal accounts (MIL-252). Business customers only — insurance customers and private
       * customers have no internal-account concept, so their lists never read it.
       */
      hideInternal: false,
      /**
       * Show only accounts with welcome emails enabled (MIL-397). Business and insurance customers
       * (`options.sendWelcomeEmails`, MIL-402) — private customers have no such concept, so their
       * list never reads it.
       */
      welcomeEmailsOnly: false,
    }),

    getters: {
      /** Whether anything is filtered — drives the toolbar button's active state. */
      isFiltering: (state): boolean =>
        state.segmentIDs.length > 0 ||
        state.currencyCodes.length > 0 ||
        state.modifiedOnly ||
        state.hideInternal ||
        state.welcomeEmailsOnly,
    },

    actions: {
      resetStore(): void {
        this.segmentIDs = []
        this.currencyCodes = []
        this.modifiedOnly = false
        this.hideInternal = false
        this.welcomeEmailsOnly = false
      },
    },
  }
)
